import { useFormik } from 'formik'
import React from 'react'
import toast from 'react-hot-toast'
import { BsChevronLeft } from 'react-icons/bs'
import { Link, useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import { useResetPasswordMutation } from '../../../features/auth/passwordSlice'

interface IResetPassword {
  code: number | null
  email: string
  setStep: React.Dispatch<React.SetStateAction<'email' | 'code' | 'password'>>
}

export const ResetPassword = ({ code, email, setStep }: IResetPassword) => {
  const [resetPassword, { isLoading, isError, error }] =
    useResetPasswordMutation()
  const navigate = useNavigate()

  const formik = useFormik({
    initialValues: {
      newPassword: '',
      confirmNewPassword: '',
    },
    validationSchema: Yup.object().shape({
      newPassword: Yup.string().required('Please create a new password'),
      confirmNewPassword: Yup.string()
        .oneOf([Yup.ref('newPassword'), null], 'Passwords must match!')
        .required('Please confirm your new password'),
    }),
    onSubmit: async (values) => {
      try {
        toast.loading('Processing...')
        await resetPassword({
          email,
          password: values.newPassword,
          code,
        }).unwrap()
        toast.dismiss()
        toast.success('Password changed successfully!')
        navigate('/login')
      } catch (error: any) {
        console.log(error)
        toast.dismiss()
        toast.error(error?.data.message)
      }
    },
  })

  return (
    <div className='bg-white w-full max-w-[400px] rounded-md shadow-lg px-5 py-10'>
      <h3 className='text-2xl text-indigo-500 font-semibold text-center'>
        Gcbuying
      </h3>
      <h4 className='text-lg font-[500] text-slate-600 mt-8'>
        Reset Your Password 🔑
      </h4>
      <p className='text-sm text-slate-500 mt-1'>
        Create new password and confirm your new password
      </p>

      <form action='' className='mt-6' onSubmit={formik.handleSubmit}>
        <div className='flex flex-col gap-1'>
          <label htmlFor='newPassword' className='text-xs text-slate-600'>
            New Password
          </label>
          <input
            type='text'
            name='newPassword'
            id='newPassword'
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            placeholder=''
            className='transition-all duration-200 px-4 py-2 rounded-md text-sm border border-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-500'
          />
          <p className='text-red-500 text-xs'>
            {formik.touched.newPassword &&
              Boolean(formik.errors.newPassword) &&
              formik.errors.newPassword}
          </p>
        </div>
        <div className='flex flex-col gap-1 mt-2'>
          <label
            htmlFor='confirmNewPassword'
            className='text-xs text-slate-600'
          >
            Confirm New Password
          </label>
          <input
            type='text'
            name='confirmNewPassword'
            id='confirmNewPassword'
            value={formik.values.confirmNewPassword}
            onChange={formik.handleChange}
            placeholder=''
            className='transition-all duration-200 px-4 py-2 rounded-md text-sm border border-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-500'
          />
          <p className='text-red-500 text-xs'>
            {formik.touched.confirmNewPassword &&
              Boolean(formik.errors.confirmNewPassword) &&
              formik.errors.confirmNewPassword}
          </p>
        </div>

        <button
          type='submit'
          className='mt-4 transition-all duration-200 w-full py-2 text-sm rounded-md text-white font-semibold bg-indigo-500 hover:brightness-105'
        >
          Change
        </button>

        <button
          type='button'
          onClick={() => setStep('code')}
          className='text-sm text-indigo-500 flex items-center mt-4 gap-2'
        >
          <BsChevronLeft className='text-md text-indigo-500' /> Back
        </button>
      </form>
    </div>
  )
}
